import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FormGroup, Collapse, Container, Row, Col, Card, Alert, CardBody, CardHeader, Button, Form, Input, Label, FormFeedback, } from "reactstrap";
import Modal from 'react-modal';


//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Navbar from 'src/components/HorizontalLayout/Navbar';
import axiosInstance from "src/common/axiosInstance";
import { toast } from 'react-toastify';
import { RootState } from "../../slices/store";
import { useSelector, useDispatch } from "react-redux";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from "react-select";

interface CorpData {
  BillingStreet: string;
  BillingCity: string;
  BillingState: string;
  BillingPostalCode: string;
  primary_contact_phone: string;
}
const CorporateInfo = () => {
  const agencyData = useSelector((state: any) => state.agency.agency.data);
  const [accountInfo, setAccountInfo] = useState<any>({});
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [col2, setCol2] = useState(true);
  const [billingCollapse, setBillingCollapse] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [editCorpData, setEditCorpData] = useState<{ [key: string]: any }>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  
  // Cache for API responses to avoid repeated calls
  const [dataCache, setDataCache] = useState<{
    countries?: any[];
    states?: { [countryCode: string]: any[] };
    cities?: { [stateId: number]: any[] };
  }>({});

  const [corpData, setCorpData] = useState<CorpData>({
    BillingStreet: '',
    BillingCity: '',
    BillingState: '',
    BillingPostalCode: '',
    primary_contact_phone: '',
  });

  useEffect(() => {
    fetchBillingContacts();
    // Pre-load countries immediately since they're needed for the modal
    fetchCountries();
  }, []);

  // Optimized modal opening with parallel data loading
  useEffect(() => {
    if (!modalOpen) return;

    const loadModalData = async () => {
      setIsLoadingModal(true);
      setEditCorpData(corpData);

      try {
        // If countries aren't cached, load them
        if (!dataCache.countries || dataCache.countries.length === 0) {
          await fetchCountries();
        }

        // If we have billing state, pre-load state and city data in parallel
        if (corpData?.BillingState) {
          const stateData = await fetchStateByName(corpData.BillingState);
          if (stateData && stateData.country_code) {
            setSelectedCountryCode(stateData.country_code);
            setSelectedStateId(stateData.id);
            
            // Load states and cities in parallel
            const [statesData, citiesData] = await Promise.all([
              fetchStates(stateData.country_code),
              fetchCities(stateData.id)
            ]);
            
            const matchedCity = citiesData?.find(city => city.name === corpData.BillingCity);
            if (matchedCity) {
              setSelectedCityId(matchedCity.id);
            }
          }
        }
      } catch (error) {
        console.error('Error loading modal data:', error);
      } finally {
        setIsLoadingModal(false);
      }
    };

    loadModalData();
  }, [modalOpen]);

  const fetchCountries = useCallback(async () => {
    // Return cached data if available
    if (dataCache.countries && dataCache.countries.length > 0) {
      setCountries(dataCache.countries);
      return dataCache.countries;
    }

    try {
      const response = await axiosInstance.get('/countries');
      const countriesData = response.data;
      setCountries(countriesData);
      setDataCache(prev => ({ ...prev, countries: countriesData }));
      return countriesData;
    } catch (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
  }, [dataCache.countries]);

  const fetchStates = useCallback(async (countryCode: string) => {
    // Return cached data if available
    if (dataCache.states?.[countryCode]) {
      setStates(dataCache.states[countryCode]);
      return dataCache.states[countryCode];
    }

    try {
      const response = await axiosInstance.get('/states', {
        params: { country_code: countryCode },
      });
      const statesData = response.data;
      setStates(statesData);
      setDataCache(prev => ({
        ...prev,
        states: { ...prev.states, [countryCode]: statesData }
      }));
      return statesData;
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  }, [dataCache.states]);

  const fetchCities = useCallback(async (regionId: number) => {
    // Return cached data if available
    if (dataCache.cities?.[regionId]) {
      setCities(dataCache.cities[regionId]);
      return dataCache.cities[regionId];
    }

    try {
      const response = await axiosInstance.get('/cities-by-state', {
        params: { region_id: regionId },
      });
      // Convert raw city data to only contain id and name
      const formattedCities: { id: number; name: string }[] = response.data.map(
        (city: any) => ({
          id: city.id,
          name: city.name,
        })
      );

      setCities(formattedCities);
      setDataCache(prev => ({
        ...prev,
        cities: { ...prev.cities, [regionId]: formattedCities }
      }));
      return formattedCities;
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  }, [dataCache.cities]);

  const fetchStateByName = useCallback(async (name: string) => {
    try {
      const response = await axiosInstance.get('/states-by-name', {
        params: { state_name: name },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching state by name:', error);
      return null;
    }
  }, []);

  const handleCountryChange = (selected: any) => {
    const code = selected?.value || "";
    setSelectedCountryCode(code);
    setStates([]);
    setCities([]);
    setSelectedStateId(null);
    setSelectedCityId(null);

    if (code) {
      // Use cached data if available, otherwise fetch
      if (dataCache.states?.[code]) {
        setStates(dataCache.states[code]);
      } else {
        fetchStates(code);
      }
    }
  };


  // Handle state selection
  // Handle state selection (for react-select)
  const handleStateChange = (selected: any) => {
    if (selected) {
      const stateId = Number(selected.value);
      const selectedState = states.find((state) => state.id === stateId);

      if (selectedState) {
        setSelectedStateId(stateId);
        setCorpData((prevData: any) => ({
          ...prevData,
          BillingState: selectedState.name, // Store full state name
          BillingCity: "", // Reset city when state changes
        }));

        setCities([]); // Reset cities
        setSelectedCityId(null); // Clear city selection
        
        // Use cached data if available, otherwise fetch
        if (dataCache.cities?.[stateId]) {
          setCities(dataCache.cities[stateId]);
        } else {
          fetchCities(stateId); // Fetch new cities
        }
      }
    } else {
      // If cleared
      setSelectedStateId(null);
      setCities([]);
      setSelectedCityId(null);
    }
  };


  // Handle city selection (store city name)
  // Handle city selection for react-select
  const handleCityChange = (selected: { value: number; label: string } | null) => {
    if (selected) {
      const cityId = Number(selected.value);
      const selectedCity = cities.find((city) => city.id === cityId);

      if (selectedCity) {
        setSelectedCityId(cityId);
        setCorpData((prevData: any) => ({
          ...prevData,
          BillingCity: selectedCity.name, // Store the full city name
        }));
      }
    } else {
      // If cleared
      setSelectedCityId(null);
      setCorpData((prevData: any) => ({
        ...prevData,
        BillingCity: '',
      }));
    }
  };


  const handleCorporateUpdate = () => {
    const originalData = {
      BillingStreet: accountInfo.BillingStreet || '',
      BillingCity: accountInfo.BillingCity || '',
      BillingState: accountInfo.BillingState || '',
      BillingPostalCode: accountInfo.BillingPostalCode || '',
      primary_contact_phone: accountInfo.primary_contact_phone || '',
    };
    // Update corporate data
    // Compare values of matching keys
    const updatedFields = Object.fromEntries(
      Object.entries(corpData).filter(([key, value]) => value !== originalData[key as keyof typeof originalData])
    );

    // Check if any changes exist
    if (Object.keys(updatedFields).length === 0) {
      toast.info("No changes to update.");
      setModalOpen(false);
      return;
    }
    if (corpData.primary_contact_phone) {
      const phoneDigits = corpData.primary_contact_phone.replace(/\D/g, ''); // Remove all non-digits
      if (phoneDigits.length !== 10) {
        setPhoneError('Please enter a valid phone number.');
        return;
      }
    }
    setPhoneError(null);
    axiosInstance.post(`/update-corp-info`, {
      sf_id: accountInfo.sf_id,
      ...corpData
    })
      .then((response) => {
        toast.success("Corporate information updated.");
        setModalOpen(false);
        setCorpData(prev => ({ ...prev, ...updatedFields }));
        fetchBillingContacts();
      })
      .catch((error) => {
        toast.error("Something Went Wrong!");
      });

    setEditCorpData({});
  };

  const handlePhoneChange = (mobile: string | '', modalName: string) => {
    setPhoneNumber(mobile);
    switch (modalName) {
      case 'corp': setCorpData((prev) => ({
        ...prev,
        primary_contact_phone: mobile || '',
      }));
        break;
      default: break;
    }
    setPhoneError(null);
  }

  const handleModalClose = () => {
    setModalOpen(false);
    setEditCorpData({});
    setSelectedCountryCode('');
    setSelectedStateId(null);
    setSelectedCityId(null);
    setPhoneNumber('');
    setStates([]);
    setCities([]);
    setPhoneError(null);

    setCorpData({
      BillingStreet: accountInfo.BillingStreet || '',
      BillingCity: accountInfo.BillingCity || '',
      BillingState: accountInfo.BillingState || '',
      BillingPostalCode: accountInfo.BillingPostalCode || '',
      primary_contact_phone: accountInfo.primary_contact_phone || '',
    });
  };


  useEffect(() => {
    if (accountInfo) {
      setCorpData({
        BillingStreet: accountInfo.BillingStreet || '',
        BillingCity: accountInfo.BillingCity || '',
        BillingState: accountInfo.BillingState || '',
        BillingPostalCode: accountInfo.BillingPostalCode || '',
        primary_contact_phone: accountInfo.primary_contact_phone || '',
      });

      setPhoneNumber(accountInfo.primary_contact_phone || '');
    }
  }, [accountInfo]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setCorpData((prevData) => {
      const key = name as keyof typeof prevData;
      if (prevData[key] === value) return prevData;
      // Update the state with the new value
      return {
        ...prevData,
        [key]: value,
      };
    });
  };

  const fetchBillingContacts = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/billing-contacts', {
        params: {
          account_ids: [agencyData.Id], // dynamic IDs here
        },
      });
      setAccountInfo(response.data[0]);

    } catch (error) {
      console.error('Error fetching billing contacts:', error);
    }
  }, []);

  // Pre-load state and city data when accountInfo is available
  useEffect(() => {
    if (accountInfo?.BillingState) {
      const preFetchStateData = async () => {
        const stateData = await fetchStateByName(accountInfo.BillingState);
        if (stateData && stateData.country_code) {
          setSelectedCountryCode(stateData.country_code);
          // Pre-load states and cities in parallel for better performance
          await Promise.all([
            fetchStates(stateData.country_code),
            fetchCities(stateData.id)
          ]);
        }
      };
      preFetchStateData();
    }
  }, [accountInfo]);
  const formatPhoneNumber = (value: string) => {
    if (!value) return "";
    const cleaned = value.replace(/\D/g, ""); // only digits
    const length = cleaned.length;

    if (length < 4) return cleaned;
    if (length < 7) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Memoized options to prevent unnecessary re-renders
  const countryOptions = useMemo(() => 
    countries.map((c) => ({ value: c.code, label: c.name, code: c.code })),
    [countries]
  );

  const stateOptions = useMemo(() => 
    states.map((s) => ({
      value: s.id,
      label: s.name.split(" - ").pop()?.trim(),
    })),
    [states]
  );

  const cityOptions = useMemo(() => 
    cities.map(c => ({ value: c.id, label: c.name })),
    [cities]
  );

  return (
    <React.Fragment>
      <Card >
        <CardHeader>
          <h5 className="mb-0"><strong>Corporate Info</strong></h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col xl={12}>
              <div className="accordion" id="accordionExample">
                <div className="accordion-item">
                  <Collapse isOpen={col2} className="accordion-collapse">
                    <div className="card shadow-sm" style={{ margin: 0 }}>
                      <div className="card-header d-flex justify-content-between align-items-center bg-light">
                        <h5 className="mb-0">{agencyData.agency_name}</h5>
                        <div className="text-primary">
                          <i
                            className="mdi mdi-pencil font-size-16 cursor-pointer accent-color"
                            onClick={() => setModalOpen(true)}
                            title="Edit"
                          ></i>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row gx-3">
                          <div className="col-md-4 d-flex flex-column mb-4">
                            <div>
                              {/* <i className="mdi mdi-map-marker-outline me-2 fs-5"></i> */}
                              <label className="d-block" style={{ marginBottom: "4px" }}>Street</label>
                            </div>
                            <div className="text-muted">
                                {accountInfo?.BillingStreet || "-"}
                            </div>
                          </div>
                          <div className="col-md-4 d-flex flex-column mb-4">
                            <div>
                              {/* <i className="mdi mdi-city me-2 fs-5"></i> */}
                              <label className="d-block" style={{ marginBottom: "4px" }}>City</label>
                            </div>
                            <div>
                                <div className="text-muted">{accountInfo?.BillingCity
                                  ? accountInfo.BillingCity.split(" - ").pop()
                                  : "-"}</div>
                            </div>
                          </div>
                          <div className="col-md-4 d-flex flex-column mb-4">
                            <div>
                              {/* <i className="mdi mdi-bank me-2 fs-5"></i> */}
                              <label className="d-block" style={{ marginBottom: "4px" }}>State</label>
                            </div>
                            <div className="text-muted">
                                {accountInfo?.BillingState
                                  ? accountInfo.BillingState.split(" - ").pop()
                                  : "-"}
                            </div>
                          </div>
                          <div className="col-md-4 d-grid align-items-start mb-2">
                            <div>
                              {/* <i className="mdi mdi-numeric me-2 fs-5"></i> */}
                              <label className="d-block" style={{ marginBottom: "4px" }}>Zip</label>
                            </div>
                            <div>
                              <div className="text-muted">
                              {accountInfo?.BillingPostalCode || "-"}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4 d-grid align-items-start mb-2">
                            <div>
                              {/* <i className="mdi mdi-phone me-2 fs-5"></i> */}
                               <label className="d-block" style={{ marginBottom: "4px" }}>Phone</label>
                            </div>
                            <div>
                              <div className="text-muted">
                                {formatPhoneNumber(accountInfo.primary_contact_phone) || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Collapse>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Corporate info edit modal */}
      <Modal isOpen={modalOpen} size="xl" className="advance-filter-modal">
        <div className="d-flex justify-content-between align-items-center pb-4">
          <h5>Edit Corporate Info</h5>
          <button onClick={handleModalClose} className="btn-close" aria-label="Close Modal">
          </button>
        </div>
        <div style={{ maxHeight: "calc(100vh - 240px)", overflowY: "auto", overflowX: "hidden" }} >
        {isLoadingModal ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <span className="ms-2">Loading location data...</span>
          </div>
        ) : (
        <div >
          {/* COUNTRY DROPDOWN */}
          <div className="row">
            <FormGroup className="mb-4 col-sm-6">
              <Label for="BillingCountry">Country</Label>
              <div className="select-wrapper">
              <Select
                className="dropdown custom-select shadow-sm rounded corporate-dropdown"
                classNamePrefix="react-select"
                id="BillingCountry"
                value={
                  countries.find((c) => c.code === selectedCountryCode)
                    ? { value: selectedCountryCode, label: countries.find((c) => c.code === selectedCountryCode)?.name }
                    : null
                }
                onChange={handleCountryChange}
                Placeholder="Select Country"
                menuPlacement={"auto"}
                options={countryOptions}
                styles={{
                  input: (provided: any) => ({
                                  ...provided,
                                  fontSize: '16px',// stops ios zoom
                                }),
                  menu: (provided: any) => ({
                    ...provided,
                    zIndex: 1050,
                    overflowX: "hidden",
                    
                  }),
                  menuList: (base: any) => ({
                    ...base,
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    maxHeight:"140px"
                  }),
                  option: (base: any) => ({
                    ...base,
                    backgroundColor: 'transparent',
                    color: 'inherit',
                    ':active': {
                      backgroundColor: 'transparent',
                    },
                  }),
                }}
              />
              </div>
            </FormGroup >
            {/* STATE DROPDOWN */}
            <FormGroup className="mb-4 col-sm-6">
              <Label for="BillingState">State</Label>
                 <div className="select-wrapper">
              <Select
                id="BillingState"
                className="dropdown custom-select shadow-sm rounded corporate-dropdown"
                classNamePrefix="react-select"
                menuPlacement={"bottom"}
                value={
                  states.find((s) => s.id === selectedStateId)
                    ? {
                      value: selectedStateId,
                      label: states.find((s) => s.id === selectedStateId)?.name.split(" - ").pop()?.trim(),
                    }
                    : null
                }
                onChange={handleStateChange}
                options={stateOptions}
                placeholder="Select State"
                styles={{
                  input: (provided: any) => ({
                                  ...provided,
                                  fontSize: '16px',// stops ios zoom
                                }),
                  menu: (provided: any) => ({
                    ...provided,
                    zIndex: 1050,
                    overflowX: "hidden",
                    
                  }),
                  menuList: (base: any) => ({
                    ...base,
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    maxHeight:"140px"
                  }),
                  option: (base: any) => ({
                    ...base,
                    backgroundColor: 'transparent',
                    color: 'inherit',
                    ':active': {
                      backgroundColor: 'transparent',
                    },
                  }),
                }}
              />
</div>
            </FormGroup>
          </div>
          {/* City Selection */}
          <div className="row" >
            <FormGroup className="mb-4 col-sm-6">
              <Label for="BillingCity">City</Label>
              <Select
              className="dropdown custom-select shadow-sm rounded corporate-dropdown"
                classNamePrefix="react-select"
                menuPlacement={"auto"}
                value={selectedCityId ? { value: selectedCityId, label: cities.find(c => c.id === selectedCityId)?.name } : null}
                onChange={handleCityChange}
                options={cityOptions}
                placeholder="Select City"
               styles={{
                input: (provided: any) => ({
                                  ...provided,
                                  fontSize: '16px',// stops ios zoom
                                }),
                  menu: (provided: any) => ({
                    ...provided,
                    zIndex: 1050,
                    overflowX: "hidden",
                    
                  }),
                  menuList: (base: any) => ({
                    ...base,
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    maxHeight:"140px"
                  }),
                  option: (base: any) => ({
                    ...base,
                    backgroundColor: 'transparent',
                    color: 'inherit',
                    ':active': {
                      backgroundColor: 'transparent',
                    },
                  }),
                }}
              />

            </FormGroup>
            <FormGroup className="mb-4 col-sm-6">
              <Label for="BillingStreet">Street</Label>
              <Input
                className="form-control"
                id="BillingStreet"
                name="BillingStreet"
                value={corpData.BillingStreet}
                onChange={handleChange}
                style={{ borderColor: 'lightgrey' }}
              />
            </FormGroup>
          </div>
          <div className="row">
            <FormGroup className="mb-4 col-sm-6">
              <Label for="BillingPostalCode">Zip</Label>
              <Input
                className="form-control"
                id="BillingPostalCode"
                name="BillingPostalCode"
                type="text"
                value={corpData.BillingPostalCode}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  handleChange(e);
                }}
                maxLength={10}
                inputMode="numeric"
                style={{ borderColor: 'lightgrey' }}
              />
            </FormGroup>
            <FormGroup className="mb-4 col-sm-6">
              <Label for="Phone">Phone</Label>
              <Input
                id="primary_contact_phone"
                name="primary_contact_phone"
                type="tel"
                value={formatPhoneNumber(corpData.primary_contact_phone)}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, ""); // only digits
                  setCorpData((prev) => ({
                    ...prev,
                    primary_contact_phone: digits,
                  }));
                }}
                maxLength={12} // 10 digits + 2 dashes
                inputMode="numeric" s
                className="form-control"
                style={{ borderColor: 'lightgrey' }}
              />
              {phoneError && (
                <div style={{ color: 'red', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  {phoneError}
                </div>)}
            </FormGroup>
          </div>
        </div>
        )}
        
        {!isLoadingModal && (
        <div className="d-flex justify-content-end gap-2 mt-2">
          <button
            type="button"
            className="btn btn-primary button-primary"
            style={{
              border: '1px solid #686868',
            }}
            onClick={handleCorporateUpdate}>Update</button>{' '}
          <button
            type="button"
            className="btn btn-danger button-secondary"
            style={{
              border: '1px solid #686868',
            }}
            onClick={handleModalClose}>Cancel</button>
        </div>
        )}
        </div>
      </Modal>
    </React.Fragment >
  );
};

export default CorporateInfo;
